/**
 * QuoteDetailPage — /quotes/:slug
 * Full-page quote view with author info, share/copy actions, and
 * related quotes by the same author.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Quote as QuoteIcon, ExternalLink, Copy, Share2, Heart, Bookmark, Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function QuoteDetailPage() {
  const { slug } = useParams();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/quotes/${slug}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const copy = () => {
    if (!data?.quote) return;
    navigator.clipboard.writeText(`"${data.quote.text}" — ${data.quote.author}`);
    toast.success("Copied to clipboard");
  };

  const share = () => {
    const url = window.location.href;
    const text = `"${data.quote.text}" — ${data.quote.author}`;
    if (navigator.share) {
      navigator.share({ title: "Quote from Social Pulse", text, url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(`${text}\n${url}`);
      toast.success("Link copied");
    }
  };

  const toggleAction = async (kind) => {
    if (!token) { toast.error("Sign in to save"); return; }
    try {
      const r = await axios.post(
        `${API}/api/quotes/${data.quote.id}/${kind}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (kind === "like") setLiked(r.data.active);
      else setBookmarked(r.data.active);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (loading) return <div className="max-w-3xl mx-auto py-16 px-4 text-center opacity-60"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>;
  if (!data) return (
    <div className="max-w-3xl mx-auto py-16 px-4 text-center">
      <h1 className="font-display text-3xl mb-2">Quote not found</h1>
      <Link to="/" className="wofy-link">← Home</Link>
    </div>
  );

  const { quote, author, related } = data;

  return (
    <div className="max-w-3xl mx-auto py-8 px-4" data-testid={`quote-page-${quote.slug}`}>
      <Link to="/" className="inline-flex items-center gap-1 text-sm opacity-70 hover:opacity-100 mb-4">
        <ArrowLeft className="w-4 h-4" /> Home
      </Link>

      {/* Hero quote */}
      <section className="wofy-card !p-10 text-center relative overflow-hidden">
        <QuoteIcon className="w-16 h-16 mx-auto opacity-10 absolute top-4 left-1/2 -translate-x-1/2" />
        <blockquote className="font-display text-3xl md:text-4xl leading-tight relative" data-testid="quote-text">
          “{quote.text}”
        </blockquote>
        <div className="mt-6 flex items-center justify-center gap-4 flex-wrap">
          <Link to={`/quotes/authors/${quote.author_slug}`} className="font-medium hover:underline" data-testid="quote-author-link">
            — {quote.author}
          </Link>
          <span className="text-xs opacity-60 px-2 py-0.5 rounded-full" style={{ background: "var(--wofy-surface)" }}>
            {quote.category_label}
          </span>
        </div>
        {quote.tags?.length > 0 && (
          <div className="mt-3 flex gap-1.5 justify-center flex-wrap">
            {quote.tags.map(t => (
              <span key={t} className="text-xs px-2 py-0.5 rounded-full" style={{ background: "var(--wofy-surface)" }}>
                #{t}
              </span>
            ))}
          </div>
        )}
      </section>

      {/* Action bar */}
      <div className="mt-4 flex gap-2 flex-wrap justify-center" data-testid="quote-actions">
        <button onClick={() => toggleAction("like")} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm hover:shadow-md" style={{ borderColor: "var(--wofy-border)", background: liked ? "#FEE2E2" : "transparent" }}>
          <Heart className={`w-4 h-4 ${liked ? "fill-red-500 text-red-500" : ""}`} />{liked ? "Liked" : "Like"}
        </button>
        <button onClick={() => toggleAction("bookmark")} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm hover:shadow-md" style={{ borderColor: "var(--wofy-border)", background: bookmarked ? "#DBEAFE" : "transparent" }}>
          <Bookmark className={`w-4 h-4 ${bookmarked ? "fill-blue-600 text-blue-600" : ""}`} />{bookmarked ? "Saved" : "Save"}
        </button>
        <button onClick={copy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm hover:shadow-md" style={{ borderColor: "var(--wofy-border)" }}>
          <Copy className="w-4 h-4" />Copy
        </button>
        <button onClick={share} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm hover:shadow-md" style={{ borderColor: "var(--wofy-border)" }}>
          <Share2 className="w-4 h-4" />Share
        </button>
      </div>

      {/* Author */}
      {author && (
        <section className="mt-8 wofy-card !p-6" data-testid="quote-author-card">
          <h2 className="font-display text-xl mb-1">About {author.name}</h2>
          <p className="text-sm opacity-80 mb-3">{author.bio || `Learn more about ${author.name} — explore all their quotes below or read the full biography on Wikipedia.`}</p>
          <div className="flex gap-2 flex-wrap">
            <Link to={`/quotes/authors/${author.slug}`} className="wofy-btn-primary text-sm inline-flex items-center gap-1">
              View all quotes ({author.quote_count})
            </Link>
            {author.wikipedia_url && (
              <a href={author.wikipedia_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-4 py-2 rounded-full border text-sm hover:shadow-md" style={{ borderColor: "var(--wofy-border)" }}>
                <ExternalLink className="w-4 h-4" />Wikipedia
              </a>
            )}
          </div>
        </section>
      )}

      {/* Related */}
      {related?.length > 0 && (
        <section className="mt-8">
          <h2 className="font-display text-xl mb-3">More from {quote.author}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {related.map(r => (
              <Link key={r.id} to={`/quotes/${r.slug}`} className="wofy-card !p-4 hover:shadow-md transition block" data-testid={`related-${r.slug}`}>
                <QuoteIcon className="w-4 h-4 opacity-50 mb-2" />
                <p className="italic text-sm leading-relaxed">“{r.text}”</p>
                <div className="mt-2 text-xs opacity-60">{r.category_label}</div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
